import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as ResetIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  RestartAlt as RestartIcon
} from '@mui/icons-material';
import { useTimer } from '../../contexts/TimerContext';
import { useLanguage } from '../../i18n/LanguageContext';
import { commonDialogStyles } from './dialogStyles';

export const TimerDisplay: React.FC = () => {
  const { 
    state, 
    timeLeft, 
    initialTime,
    pauseTimer, 
    resumeTimer, 
    resetTimer, 
    stopTimer,
    configureTimer,
    restartTimer,
    formatTime 
  } = useTimer();
  const { t } = useLanguage();
  
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configMinutes, setConfigMinutes] = useState('');
  const [configSeconds, setConfigSeconds] = useState('');

  const handleConfigureOpen = () => {
    // Pre-populate with current timer values
    const minutes = Math.floor(initialTime / 60);
    const seconds = initialTime % 60;
    setConfigMinutes(minutes.toString());
    setConfigSeconds(seconds.toString());
    setConfigDialogOpen(true);
  };

  const handleConfigureClose = () => {
    setConfigDialogOpen(false);
    setConfigMinutes('');
    setConfigSeconds('');
  };

  const handleConfigureSubmit = () => {
    const minutes = parseInt(configMinutes) || 0;
    const seconds = parseInt(configSeconds) || 0;
    const totalSeconds = minutes * 60 + seconds;
    
    if (totalSeconds > 0) {
      configureTimer(totalSeconds);
    }
    handleConfigureClose();
  };

  const handleQuickTime = (seconds: number) => {
    configureTimer(seconds);
    handleConfigureClose();
  };

  const getTimerColor = () => {
    if (state === 'finished') return 'error.main';
    if (state === 'paused') return 'warning.main';
    if (timeLeft <= 10) return 'error.main';
    if (timeLeft <= 30) return 'warning.main';
    return 'success.main';
  };

  const getTimerStatus = () => {
    if (state === 'finished') return t('timer.finished');
    if (state === 'paused') return t('timer.paused');
    if (state === 'running') return t('timer.running');
    return t('timer.stopped');
  };

  // Don't render if timer is not active
  if (state === 'stopped') {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: state === 'finished' ? 'error.main' : 'divider',
          borderRadius: 2,
          p: 2,
          mb: 2,
          boxShadow: state === 'finished' ? 3 : 1,
          animation: state === 'finished' ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.7 },
            '100%': { opacity: 1 }
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                color: getTimerColor(),
                fontFamily: 'monospace'
              }}
            >
              {formatTime(timeLeft)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {getTimerStatus()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {state === 'finished' ? (
              <Tooltip title={t('timer.restart')}>
                <IconButton onClick={restartTimer} color="success">
                  <RestartIcon />
                </IconButton>
              </Tooltip>
            ) : state === 'running' ? (
              <Tooltip title={t('timer.pause')}>
                <IconButton onClick={pauseTimer} color="warning">
                  <PauseIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title={t('timer.resume')}>
                <IconButton onClick={resumeTimer} color="success">
                  <PlayIcon />
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title={t('timer.reset')}>
              <IconButton onClick={resetTimer} color="info">
                <ResetIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('timer.configure')}>
              <IconButton onClick={handleConfigureOpen} color="primary">
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={t('timer.stop')}>
              <IconButton onClick={stopTimer} color="error">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Dialog 
        open={configDialogOpen} 
        onClose={handleConfigureClose}
        maxWidth="sm"
        fullWidth
        sx={commonDialogStyles}
      >
        <DialogTitle>{t('timer.configure')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('timer.quickTimes')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label="30s" 
                clickable 
                onClick={() => handleQuickTime(30)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label="60s" 
                clickable 
                onClick={() => handleQuickTime(60)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label="90s" 
                clickable 
                onClick={() => handleQuickTime(90)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label="2m" 
                clickable 
                onClick={() => handleQuickTime(120)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label="3m" 
                clickable 
                onClick={() => handleQuickTime(180)}
                color="primary"
                variant="outlined"
              />
              <Chip 
                label="5m" 
                clickable 
                onClick={() => handleQuickTime(300)}
                color="primary"
                variant="outlined"
              />
            </Stack>
            
            <Typography variant="subtitle1" gutterBottom>
              {t('timer.customTime')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label={t('timer.minutes')}
                type="number"
                value={configMinutes}
                onChange={(e) => setConfigMinutes(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{ flex: 1 }}
              />
              <Typography variant="h6">:</Typography>
              <TextField
                label={t('timer.seconds')}
                type="number"
                value={configSeconds}
                onChange={(e) => setConfigSeconds(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfigureClose}>
            {t('timer.cancel')}
          </Button>
          <Button 
            onClick={handleConfigureSubmit} 
            variant="contained"
            disabled={!configMinutes && !configSeconds}
          >
            {t('timer.setTime')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 